import { useDismiss, useInteractions } from '@floating-ui/react';
import { useLayoutEffect } from 'react';
import { useAngular } from 'src/hooks/use-angular';
import type { UseFloatingProps } from 'src/hooks/use-floating';
import { useFloating } from 'src/hooks/use-floating';

/**
 * This hook is a wrapper around `useFloating` that preconfigures the `useDismiss` behavior.
 *
 * Originally it contained a lot of custom code and worked quite differently then `useFloating`.
 * By now the logic was consolidated a lot, but for the moment it still makes sense to keep it
 * as a separate hook, because the usage with `getReferenceProps` and `getFloatingProps` is different.
 */
export function useDropdown(options: UseFloatingProps) {
  const floating = useFloating(options);

  const dismiss = useDismiss(floating.context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  // iframes need custom interaction handling
  const angular = useAngular();
  useLayoutEffect(() => {
    const contentWindow = angular.iframe?.contentWindow;
    if (!contentWindow) return;
    if (!floating.open) return;
    const handlePointerDown = () => floating.setOpen(false);
    contentWindow.addEventListener('pointerdown', handlePointerDown);
    return () => {
      contentWindow.removeEventListener('pointerdown', handlePointerDown);
    };
    // oxlint-disable-next-line exhaustive-deps
  }, [floating.open, angular.iframe]);

  return { getReferenceProps, getFloatingProps, ...floating };
}

export type DropdownState = ReturnType<typeof useDropdown>;
